import React from 'react';
import { Modal } from '@/components/Modals/modal';
import { ModalBody, ModalImpl } from '../ModalFactory';
import { MapPin, Edit2, Plus, Search, Navigation } from 'lucide-react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  ArtisanalSelect,
  SelectOption,
} from '@/components/Input/Dropdown/Select/Select';
import useConfirmAddress, {
  useAddressSuggestions,
} from '@/hooks/useConfirmAddress';
import { Recipient } from '@/context/types';
import { useAppDispatch } from '@/context/appState';

type LocationPopoverData = {
  anchorRect: DOMRect;
  currentAddress?: Recipient;
  onAddressConfirmed: (details: any) => Promise<void>;
};

export const LocationPopoverModal: ModalImpl = (props) => {
  return (
    <Modal
      // Reusing the date-picker variant to inherit the exact same spring physics and relative positioning
      variant="date-picker"
      ariaLabel="Delivery Location"
      // CRITICAL: Must be overflow-visible so the ArtisanalSelect dropdown menu can break out of the popover boundaries
      className="!p-0 !overflow-visible w-full h-fit"
      darkenOverlay={false}
      {...props}
    >
      {LocationPopoverBody}
    </Modal>
  );
};

const LocationPopoverBody: ModalBody<LocationPopoverData> = ({
  modalData,
  requestModalClose,
}) => {
  const dispatch = useAppDispatch();
  const { getSuggestions } = useAddressSuggestions();

  // Wire up the confirm hooks natively
  const { selectAddress, openManualAddress } = useConfirmAddress({
    onAddressConfirmed: modalData?.onAddressConfirmed,
  });

  const hasCurrentAddress = Boolean(modalData?.currentAddress?.street);

  // 1. Mapbox Dropdown Renderer (Identical to Delivery.tsx)
  const renderAddressOption = (data: SelectOption, { isSelected }: any) => {
    const labelParts = data.label?.split(',');
    const primaryLine = labelParts[0];
    const secondaryLine = labelParts.slice(1).join(',').trim();

    return (
      <div className="flex flex-col">
        <span
          className={`transition-colors whitespace-nowrap overflow-hidden text-ellipsis leading-tight ${isSelected ? 'font-[700] text-black' : 'font-[500] text-text-main'}`}
        >
          {primaryLine}
        </span>
        {secondaryLine && (
          <span className="text-[12px] text-text-muted whitespace-nowrap overflow-hidden text-ellipsis mt-0.5">
            {secondaryLine}
          </span>
        )}
      </div>
    );
  };

  // 2. Dispatcher to edit the already populated address
  const handleEditCurrent = () => {
    if (!modalData?.currentAddress) return;

    // Dispatching OPEN_ADDRESS_CONFIRM automatically replaces this popover with the Form Modal
    dispatch({
      type: 'OPEN_ADDRESS_CONFIRM',
      fullName: modalData.currentAddress.fullName || '',
      address: modalData.currentAddress,
      instructions: modalData.currentAddress.instructions || '',
      onAddressConfirmed: modalData.onAddressConfirmed,
    });
  };

  return (
    <div className="flex flex-col w-full bg-brand-cream text-text-main rounded-[var(--radius-artisan)]">
      {/* HEADER */}
      <div className="p-4 border-b border-brand-truffle/5 bg-brand-cream/50 rounded-t-[var(--radius-artisan)]">
        <h3 className="font-display font-bold text-lg text-text-heading flex items-center gap-2">
          <Navigation size={18} className="text-brand-truffle" />
          Delivery Details
        </h3>
      </div>

      {/* BODY */}
      <div className="p-4 flex flex-col gap-5 w-full">
        {/* Search Section */}
        <div className="flex flex-col gap-2 w-full">
          <label className="font-body text-xs font-bold uppercase tracking-widest text-text-muted flex items-center gap-1.5">
            <Search size={12} /> Find Address
          </label>
          <ArtisanalSelect
            loadOptions={getSuggestions}
            formatOptionLabel={renderAddressOption}
            onChange={selectAddress} // Automatically opens the Edit Modal upon selection
            placeholder="Search your street or zip..."
            // ADD THESE TWO PROPS:
            // 1. Portals the dropdown to the root HTML body
            menuPortalTarget={document.body}
            // 2. Forces fixed calculation to ignore Framer Motion's transform CSS
            menuPosition="fixed"
          />
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 opacity-60">
          <div className="h-px flex-1 bg-brand-truffle/20" />
          <span className="font-display text-xs font-bold text-text-muted uppercase tracking-widest">
            or
          </span>
          <div className="h-px flex-1 bg-brand-truffle/20" />
        </div>

        {/* Current Address / Manual Entry Section */}
        {hasCurrentAddress ? (
          <div className="flex flex-col gap-2">
            <label className="font-body text-xs font-bold uppercase tracking-widest text-text-muted flex items-center gap-1.5">
              <MapPin size={12} /> Current Selection
            </label>
            <div
              onClick={handleEditCurrent}
              className="flex items-center justify-between p-3 rounded-[var(--radius-artisan)] border border-brand-truffle/10 bg-brand-frost/20 hover:bg-brand-cream cursor-pointer transition-colors group"
            >
              <div className="flex flex-col truncate pr-4">
                <span className="font-[700] text-sm text-text-heading truncate">
                  {modalData.currentAddress?.street}{' '}
                  {modalData.currentAddress?.secondary_address}
                </span>
                <span className="text-xs text-text-muted truncate">
                  {modalData.currentAddress?.place},{' '}
                  {modalData.currentAddress?.region_code}{' '}
                  {modalData.currentAddress?.postcode}
                </span>
              </div>
              <button className="h-8 w-8 rounded-full bg-white shadow-sm flex items-center justify-center text-brand-truffle group-hover:bg-brand-truffle group-hover:text-white transition-colors shrink-0">
                <Edit2 size={14} />
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={openManualAddress}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-[var(--radius-artisan)] border border-brand-truffle/20 text-brand-truffle hover:bg-brand-truffle/5 font-display font-bold text-sm tracking-widest uppercase transition-colors"
          >
            <Plus size={16} /> Enter Manually
          </button>
        )}
      </div>
    </div>
  );
};
